import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Filter, Loader2, ArrowRight } from "lucide-react";
import { formatApiError } from "./AuthContext";
import { StagePill } from "./AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCES = [
  { key: "", label: "All sources" },
  { key: "growth-assessment", label: "Growth Assessments" },
  { key: "discovery-booking", label: "Discovery Bookings" },
];

export default function LeadsList() {
  const [params, setParams] = useSearchParams();
  const source = params.get("source") || "";
  const stage = params.get("stage") || "";
  const q = params.get("q") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(q);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        const query = new URLSearchParams();
        if (source) query.set("source", source);
        if (stage) query.set("stage", stage);
        if (q) query.set("q", q);
        query.set("limit", "500");
        const r = await axios.get(`${API}/admin/leads?${query}`);
        setData(r.data);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [source, stage, q]);

  const applySearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search) next.set("q", search);
    else next.delete("q");
    setParams(next);
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const stages = data?.stages || [];

  const counts = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.leads.forEach((l) => {
      map[l.stage] = (map[l.stage] || 0) + 1;
    });
    return map;
  }, [data]);

  return (
    <div data-testid="admin-leads" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">Leads</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">
          All leads
        </h1>
        <p className="mt-2 text-mid-neutral">
          Unified inbox of Growth Assessments and Discovery Bookings.
        </p>
      </header>

      <div className="flex flex-col md:flex-row gap-3">
        <form onSubmit={applySearch} className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-mid-neutral" />
          <input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, business, phone…"
            className="w-full h-12 pl-11 pr-4 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest"
          />
        </form>

        <select
          value={source}
          onChange={(e) => setFilter("source", e.target.value)}
          data-testid="leads-filter-source"
          className="h-12 px-4 rounded-xl bg-white border border-border-neutral text-sm text-forest focus:outline-none focus:border-mint"
        >
          {SOURCES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>

        <select
          value={stage}
          onChange={(e) => setFilter("stage", e.target.value)}
          data-testid="leads-filter-stage"
          className="h-12 px-4 rounded-xl bg-white border border-border-neutral text-sm text-forest focus:outline-none focus:border-mint"
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-40 text-mid-neutral">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          <p className="text-xs text-mid-neutral">
            Showing <strong className="text-forest">{data.total}</strong> lead{data.total === 1 ? "" : "s"}
          </p>

          <div className="rounded-2xl bg-white border border-border-neutral overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-soft/60 text-[10px] font-bold uppercase tracking-[0.15em] text-mid-neutral">
              <div className="col-span-3">Contact</div>
              <div className="col-span-3">Business</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Stage</div>
              <div className="col-span-2 text-right">Received</div>
            </div>
            {data.leads.length === 0 && (
              <div className="p-8 text-center text-sm text-mid-neutral">
                No leads match these filters.
              </div>
            )}
            {data.leads.map((l) => (
              <Link
                key={l.id}
                to={`/admin/leads/${l.id}`}
                data-testid={`lead-row-${l.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center px-5 py-4 border-t border-border-neutral hover:bg-soft/40 transition-colors"
              >
                <div className="md:col-span-3">
                  <p className="font-bold text-forest">
                    {l.firstName} {l.lastName || ""}
                  </p>
                  <p className="text-xs text-mid-neutral truncate">{l.email}</p>
                </div>
                <div className="md:col-span-3 text-sm text-forest truncate">
                  {l.businessName || <span className="text-mid-neutral">—</span>}
                </div>
                <div className="md:col-span-2 text-xs text-mid-neutral truncate">
                  {l.sourceLabel}
                </div>
                <div className="md:col-span-2">
                  <StagePill stage={l.stage} />
                </div>
                <div className="md:col-span-2 md:text-right text-xs text-mid-neutral flex md:justify-end items-center gap-1">
                  {formatDate(l.createdAt)}
                  <ArrowRight className="h-3.5 w-3.5 hidden md:inline text-forest/40" />
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
}
